import type { IntelTechnologiesSortBy, SortDirection } from '@@types/api.types';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORTING,
  deriveSort,
} from '@components/core/intel-technologies-table/intel-technologies-sort.util';
import type { FiltersState } from '@components/data-table-filter/core/types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsJson, parseAsString, useQueryState } from 'nuqs';
import { type SetStateAction, useCallback, useMemo } from 'react';
import { z } from 'zod';

type IntelTechnologiesCatalogQueryParams = {
  categoryIds?: string[];
  companyIds?: string[];
  domainIds?: string[];
  limit: number;
  mrlMax?: number;
  mrlMin?: number;
  page: number;
  search?: string;
  sortBy?: IntelTechnologiesSortBy;
  sortDirection?: SortDirection;
  subcategoryIds?: string[];
  trlMax?: number;
  trlMin?: number;
  useIds?: string[];
};

type UseIntelTechnologiesCatalogQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  filtersState: FiltersState;
  handleFiltersChange: (filters: SetStateAction<FiltersState>) => void;
  handleLimitChange: (newLimit: number) => void;
  limit: number;
  mrlMax: number | null;
  mrlMin: number | null;
  page: number;
  queryParams: IntelTechnologiesCatalogQueryParams;
  search: string | null;
  setMrlMax: (value: number | null) => Promise<URLSearchParams>;
  setMrlMin: (value: number | null) => Promise<URLSearchParams>;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState | null) => Promise<URLSearchParams>;
  setTrlMax: (value: number | null) => Promise<URLSearchParams>;
  setTrlMin: (value: number | null) => Promise<URLSearchParams>;
  sortingState: SortingState;
  trlMax: number | null;
  trlMin: number | null;
};

const isFiltersState = (value: unknown): value is FiltersState =>
  Array.isArray(value) &&
  value.every(
    (item): boolean =>
      typeof item === 'object' &&
      item !== null &&
      'columnId' in item &&
      typeof item.columnId === 'string' &&
      'values' in item &&
      Array.isArray(item.values),
  );

// The Intel technologies list endpoint only accepts OR-within inclusion (D2/D6); coerce every facet
// operator back to its inclusion variant so the filter UI's operator switch cannot silently request
// unsupported exclude/all-of semantics that would be serialized as plain inclusion.
const normalizeInclusionOperators = (filters: FiltersState): FiltersState =>
  filters.map((filter) =>
    filter.type === 'multiOption'
      ? { ...filter, operator: filter.values.length > 1 ? 'include any of' : 'include' }
      : filter,
  );

const getStringFilterValues = (filters: FiltersState, columnId: string): string[] | undefined => {
  const filter = filters.find((item) => item.columnId === columnId);
  const values = filter?.values.filter((value): value is string => typeof value === 'string') ?? [];

  return values.length > 0 ? values : undefined;
};

export const useIntelTechnologiesCatalogQueryParams = (): UseIntelTechnologiesCatalogQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [mrlMax, setMrlMax] = useQueryState('mrlMax', parseAsInteger);
  const [mrlMin, setMrlMin] = useQueryState('mrlMin', parseAsInteger);
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));
  const [trlMax, setTrlMax] = useQueryState('trlMax', parseAsInteger);
  const [trlMin, setTrlMin] = useQueryState('trlMin', parseAsInteger);
  const [filtersState, setFiltersState] = useQueryState<FiltersState>(
    'filters',
    parseAsJson(z.custom<FiltersState>(isFiltersState)).withDefault([]),
  );

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const queryParams = useMemo(() => {
    const { sortBy, sortDirection } = deriveSort(sortingState);

    return {
      categoryIds: getStringFilterValues(filtersState, 'categoryIds'),
      companyIds: getStringFilterValues(filtersState, 'companyIds'),
      domainIds: getStringFilterValues(filtersState, 'domainIds'),
      limit: limit ?? DEFAULT_PAGE_SIZE,
      mrlMax: mrlMax ?? undefined,
      mrlMin: mrlMin ?? undefined,
      page: page ?? 1,
      search: search ?? undefined,
      sortBy,
      sortDirection,
      subcategoryIds: getStringFilterValues(filtersState, 'subcategoryIds'),
      trlMax: trlMax ?? undefined,
      trlMin: trlMin ?? undefined,
      useIds: getStringFilterValues(filtersState, 'useIds'),
    };
  }, [filtersState, limit, mrlMax, mrlMin, page, search, sortingState, trlMax, trlMin]);

  const handleFiltersChange = useCallback(
    (filters: SetStateAction<FiltersState>) => {
      void setFiltersState((prev) =>
        normalizeInclusionOperators(typeof filters === 'function' ? filters(prev) : filters),
      );
      void setPage(1);
    },
    [setFiltersState, setPage],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    filtersState,
    handleFiltersChange,
    handleLimitChange,
    limit: limit ?? DEFAULT_PAGE_SIZE,
    mrlMax,
    mrlMin,
    page: page ?? 1,
    queryParams,
    search,
    setMrlMax,
    setMrlMin,
    setPage,
    setSearch,
    setSorting,
    setTrlMax,
    setTrlMin,
    sortingState,
    trlMax,
    trlMin,
  };
};
